/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { ApprovalRounded, Edit } from '@mui/icons-material';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';
import useForm from '../../common/hooks/useForm';

import { convertToSubsidyYTDInput, epochToDate, formatDate } from '../../common/UtilityFunctions';

type EditSubsidyFormProps = SubsidyObj & {
  updateLoanSubsidy: (
    fields: Record<string, any>,
    setErrors: Dispatch<SetStateAction<Record<string, any>>>,
  ) => Promise<void>;
};

function EditSubsidyForm({
  loan,
  cnt,
  subsidyName,
  subsidyType,
  monthlyAmount,
  startDate,
  endDate,
  terminationDate,
  agencySubsidyBalance,
  agencySubsidyPaidToDate,
  YTD,
  approvedAmount,
  agencyCureAmount,
  totalAgencySubsidyAmount,
  updateLoanSubsidy,
}: EditSubsidyFormProps) {
  const [isEditing, setIsEditing] = useState(false);
  const ref = useRef();
  const { fields, setFields, errors, isSubmitting, handleChange, handleSubmit } = useForm(
    {
      monthlyAmount,
      startDate,
      endDate,
      terminationDate,
      approvedAmount,
      agencyCureAmount,
      totalAgencySubsidyAmount,
    },
    updateLoanSubsidy,
  );

  const toggleIsEditing = useCallback(() => {
    const fields = {
      loan,
      cnt,
      subsidyName,
      subsidyType,
      monthlyAmount,
      startDate: startDate ? epochToDate(startDate).toISOString().split('T')[0] : '',
      endDate: endDate ? epochToDate(endDate).toISOString().split('T')[0] : '',
      terminationDate: terminationDate ? epochToDate(terminationDate).toISOString().split('T')[0] : '',
      agencySubsidyBalance,
      agencySubsidyPaidToDate,
      YTD,
      approvedAmount,
      agencyCureAmount,
      totalAgencySubsidyAmount,
    };

    console.log(fields);
    setFields(fields);
    setIsEditing((prev) => !prev);
  }, [
    monthlyAmount,
    startDate,
    endDate,
    terminationDate,
    approvedAmount,
    agencyCureAmount,
    totalAgencySubsidyAmount,
    setFields,
  ]);

  return isEditing ? (
    <tr>
      <td colSpan={8}>
        <table>
          <tbody ref={ref}>
            <tr>
              <td>Subsidy Name</td>
              <td>
                <input disabled type='text' name='Name' value={fields.subsidyName} onChange={handleChange} />
                {errors.subsidyName && <small>{errors.subsidyName}</small>}
              </td>
            </tr>
            <tr>
              <td>Subsidy Type</td>
              <td>
                <input disabled type='text' name='Type' value={fields.subsidyType} onChange={handleChange} />
                {errors.subsidyType && <small>{errors.subsidyType}</small>}
              </td>
            </tr>
            <tr>
              <td>Monthly Amount</td>
              <td>
                <input type='number' name='monthlyAmount' value={fields.monthlyAmount} onChange={handleChange} />
                {errors.monthlyAmount && <small>{errors.monthlyAmount}</small>}
              </td>
            </tr>
            <tr>
              <td>Starting Due Date (1st of month)</td>
              <td>
                <input type='date' name='startDate' value={fields.startDate} onChange={handleChange} />
                {errors.startDate && <small>{errors.startDate}</small>}
              </td>
            </tr>
            <tr>
              <td>Last Due Date (1st of month)</td>
              <td>
                <input type='date' name='endDate' value={fields.endDate} onChange={handleChange} />
                {errors.endDate && <small>{errors.endDate}</small>}
              </td>
            </tr>
            <tr>
              <td>Date Terminated</td>
              <td>
                <input type='date' name='terminationDate' value={fields.terminationDate} onChange={handleChange} />
                {errors.terminationDate && <small>{errors.terminationDate}</small>}
              </td>
            </tr>
            <tr>
              <td>Balance</td>
              <td>
                <input
                  disabled
                  type='number'
                  name='agencySubsidyBalance'
                  value={fields.agencySubsidyBalance}
                  onChange={handleChange}
                />
                {errors.agencySubsidyBalance && <small>{errors.agencySubsidyBalance}</small>}
              </td>
            </tr>
            <tr>
              <td>Paid to Date</td>
              <td>
                <input
                  disabled
                  type='number'
                  name='agencySubsidyPaidToDate'
                  value={fields.agencySubsidyPaidToDate}
                  onChange={handleChange}
                />
                {errors.agencySubsidyPaidToDate && <small>{errors.agencySubsidyPaidToDate}</small>}
              </td>
            </tr>
            <tr>
              <td>Year to Date</td>
              <td>{convertToSubsidyYTDInput(fields.YTD)}</td>
            </tr>
            <tr>
              <td>Approved Amount</td>
              <td>
                <input type='number' name='approvedAmount' value={fields.approvedAmount} onChange={handleChange} />
                {errors.approvedAmount && <small>{errors.approvedAmount}</small>}
              </td>
            </tr>
            <tr>
              <td>Amount Used to Cure Delinquency</td>
              <td>
                <input type='number' name='agencyCureAmount' value={fields.agencyCureAmount} onChange={handleChange} />
                {errors.agencyCureAmount && <small>{errors.agencyCureAmount}</small>}
              </td>
            </tr>
            <tr>
              <td>Total Amount</td>
              <td>
                <input
                  type='number'
                  name='totalAgencySubsidyAmount'
                  value={fields.totalAgencySubsidyAmount}
                  onChange={handleChange}
                />
                {errors.totalAgencySubsidyAmount && <small>{errors.totalAgencySubsidyAmount}</small>}
              </td>
            </tr>
            <tr>
              <td>
                {isSubmitting ? (
                  <CircularProgress size='1.5rem' />
                ) : (
                  <Tooltip title='Save'>
                    <IconButton
                      onClick={(e) => {
                        handleSubmit(e);
                        toggleIsEditing();
                      }}
                    >
                      <ApprovalRounded />
                      Save
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title='Cancel'>
                  <IconButton onClick={toggleIsEditing}>
                    <ApprovalRounded />
                    Cancel
                  </IconButton>
                </Tooltip>
              </td>
              <td colSpan={3}>
                <p>* Indicates required field</p>
                {errors.generic && <small>{errors.generic}</small>}
              </td>
            </tr>
          </tbody>
        </table>
      </td>
    </tr>
  ) : (
    <tr>
      <td>
        <Tooltip title='Edit'>
          <IconButton onClick={toggleIsEditing}>
            <Edit />
          </IconButton>
        </Tooltip>
        {subsidyName}-{subsidyType}
      </td>
      <td>{monthlyAmount}</td>
      <td>{startDate && formatDate(epochToDate(startDate))}</td>
      <td>{endDate && formatDate(epochToDate(endDate))}</td>
      <td>{terminationDate && formatDate(epochToDate(terminationDate))}</td>
      <td>{agencySubsidyBalance}</td>
      <td>{agencySubsidyPaidToDate}</td>
      <td>{convertToSubsidyYTDInput(YTD)}</td>
      <td>{approvedAmount}</td>
      <td>{agencyCureAmount}</td>
      <td>{totalAgencySubsidyAmount}</td>
    </tr>
  );
}

export default EditSubsidyForm;
